import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

/**
 * PageHeader — the one page-title bar for the app. Generalizes the legacy
 * header species (CrmListPage gradient hero, FormHero, back-button bars):
 *
 *   variant="hero"  (default) — vertical accent bar + icon + title/subtitle
 *   variant="plain"           — no accent bar; title row only
 *
 * Slots: `actions` (right side), `meta` (extra node under the title),
 * `backTo` (path) or `onBack` (handler) for a leading back button.
 */
const PageHeader = ({
  icon: Icon,
  title,
  subtitle,
  meta,
  actions,
  backTo,
  onBack,
  gradient,
  variant,
  className,
  children
}) => {
  const navigate = useNavigate();
  const handleBack = onBack || (backTo ? () => navigate(backTo) : null);

  return (
    <div
      className={classNames(
        'tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3',
        className
      )}
    >
      <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-0">
        {handleBack && (
          <button
            type="button"
            onClick={handleBack}
            title="Back"
            aria-label="Back"
            className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-border tw-border-solid tw-border-slate-300 tw-bg-white tw-text-slate-600 hover:tw-bg-slate-50 tw-flex-shrink-0"
          >
            <FaArrowLeft size={13} />
          </button>
        )}
        {variant === 'hero' && (
          <div
            className={classNames(
              'tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0',
              !gradient && 'tw-bg-gradient-to-b tw-from-primary-500 tw-to-primary-700'
            )}
            style={gradient ? { background: gradient } : undefined}
          />
        )}
        {Icon && <Icon className="tw-text-primary-600 tw-flex-shrink-0" size={20} />}
        <div className="tw-min-w-0">
          <h1 className="tw-mb-0 tw-font-bold tw-text-xl tw-text-slate-900 tw-truncate">
            {title}
          </h1>
          {subtitle && (
            <p className="tw-text-slate-500 tw-text-sm tw-mb-0">{subtitle}</p>
          )}
          {meta}
        </div>
      </div>
      {(actions || children) && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
          {actions || children}
        </div>
      )}
    </div>
  );
};

PageHeader.propTypes = {
  icon: PropTypes.elementType,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  meta: PropTypes.node,
  actions: PropTypes.node,
  backTo: PropTypes.string,
  onBack: PropTypes.func,
  gradient: PropTypes.string,
  variant: PropTypes.oneOf(['hero', 'plain']),
  className: PropTypes.string,
  children: PropTypes.node
};

PageHeader.defaultProps = {
  variant: 'hero'
};

export default PageHeader;
